import {
  SCOTSMAN_DIMENSION_COUNT,
  SCOTSMAN_DIMENSIONS,
} from '../../features/opportunities/opportunity-wizard/steps/scotsman/scotsman-dimensions.config';
import type {
  ScotsmanDimension,
  ScotsmanRagStatus,
} from '../../features/opportunities/opportunity-wizard/steps/scotsman/scotsman.domain';
import type { SmartDiscoveryEvidenceReference } from '../../features/opportunities/opportunity-wizard/steps/smart-discovery/models/smart-discovery.models';
import type {
  ScotsmanAnalyzerStatus,
  ScotsmanAssessmentApiResponse,
  ScotsmanDimensionAssessmentApiResponse,
  ScotsmanHumanDimensionPatchRequest,
} from '../../features/opportunities/opportunity-wizard/steps/scotsman/scotsman-api/scotsman-qualification-api.models';
import { getScotsmanMockTiming } from './scotsman-api-mock.timing';
import { mockQualificationMaterialChangeById } from './qualification-mock.helpers';
import {
  mockMarkBidRiskRunning,
  mockResetOpportunityValuePoll,
} from './opportunity-assessment-api-mock.handlers';

export interface ScotsmanMockHandlerResult {
  status: number;
  body: unknown;
  delayMs?: number;
}

const SCOTSMAN_ASSESSMENT_GET_PATH = /^\/api\/opportunities\/([^/]+)\/scotsman\/assessment$/;
const SCOTSMAN_ASSESSMENT_FINALIZE_PATH =
  /^\/api\/opportunities\/([^/]+)\/scotsman\/assessment\/finalize$/;
const SCOTSMAN_ASSESSMENT_ANALYZE_PATH =
  /^\/api\/opportunities\/([^/]+)\/scotsman\/assessment\/analyze$/;
const SCOTSMAN_DIMENSION_PATCH_PATH =
  /^\/api\/opportunities\/([^/]+)\/scotsman\/assessment\/dimensions\/([^/]+)$/;
const SCOTSMAN_ACKNOWLEDGE_AI_CHANGE_PATH =
  /^\/api\/opportunities\/([^/]+)\/scotsman\/assessment\/dimensions\/([^/]+)\/acknowledge-ai-suggestion-change$/;

/** Mock-only scoring weights (mirror backend ScotsmanDimensionWeights; total 100). */
const MOCK_DIMENSION_WEIGHTS: Record<ScotsmanDimension, number> = {
  Solution: 14,
  Competition: 12,
  Originality: 10,
  Timescale: 8,
  Size: 8,
  Money: 14,
  Authority: 16,
  Need: 18,
};

const MOCK_AI_RATIONALE_BY_DIMENSION: Record<ScotsmanDimension, string> = {
  Solution: `**Solution**

Clear fit: RFP requests digital platform modernisation using a cloud-native stack. Client goal is to reduce manual processing time by 60% and meet a regulatory integration deadline in Q4 2026.

**Capabilities**

Zuhlke has comparable delivery references in transport and strong cloud-native delivery experience. Minor gaps may exist in niche compliance tooling — validate before submission.

**Contractual / Legal**

-not yet implemented`,
  Competition: `**Process**

Open tender with published evaluation criteria and a defined shortlist stage. Process appears structured though not all dialogue dates are confirmed.

**Incumbent & Fairness**

No stated preferred supplier and no obvious incumbent lock-in in the pack.

**Competition**

A major systems integrator and a regional IT firm are likely bidding based on industry pattern; competitive field detail is thin.

**Relationship**

-not yet implemented`,
  Originality: `**Uniqueness**

Sector references exist but no established brand recognition with this buying group yet.

**Winning Edge**

No pitch or workshop is documented in discovery — confirm whether evaluation is paper-only.

**Win Theme Fit**

Win themes are not explicit in the pack — map themes in the bid strategy session.`,
  Timescale: `**Decision timeline**

Submission window is tight given solution-design depth required in the RFP.

**Realistic expectations**

Phasing is not fully specified; delivery runway needs validation with scope owners.

**Urgency**

Regulatory integration deadline creates credible delivery pressure.`,
  Size: `**Deal contribution**

Deal scale appears meaningful versus company pipeline targets based on stated programme size.

**Account potential**

RFP signals potential for a multi-year maintenance phase and broader digital programme follow-on.

**Capacity**

Core delivery shape fits our model; confirm lead architect availability before committing bid effort.`,
  Money: `**Client budget**

Budget is explicitly stated as approved in the RFP documentation.

**Pricing**

Pricing method is not specified — fixed price or T&M both appear viable given project type.

**Willingness to pay**

Funding language is positive; economic buyer engagement still needs confirmation.

**Profitability**

Profitability cannot be assessed from discovery alone — run pricing review.`,
  Authority: `**Decision-maker known**

Our contact is the Digital Transformation Lead — not the final economic decision-maker.

**Internal champion**

No active sponsor advocating for Zühlke is visible in discovery.

**Decision maker awareness**

Procurement committee and CTO sign-off are implied but not evidenced as engaged.

**Stakeholder coverage**

Coverage is thin — plan multi-thread outreach before submission.`,
  Need: `**Expressed business need**

Regulatory integration deadline forces action; business need is externally driven and non-negotiable.

**Quantified business impact**

Cost of inaction is articulated in the RFP preamble with a 60% processing-time reduction target.

**Strategic priority**

Programme aligns to digital transformation priorities though executive sponsorship is not named.

**Change necessity / status-quo broken**

Status quo is broken by regulatory deadline — change is required, not optional.`,
};

function mockEvidenceForDimension(dimension: ScotsmanDimension): SmartDiscoveryEvidenceReference[] {
  const definition = SCOTSMAN_DIMENSIONS.find((row) => row.dimension === dimension);
  const name = definition?.name ?? dimension;
  const primary: SmartDiscoveryEvidenceReference = {
    id: `chunk:scotsman-mock-${dimension.toLowerCase()}-1`,
    label: `${name} — workspace excerpt`,
    documentTitle: 'Client opportunity pack (mock).pdf',
    sectionPath: ['Scope', 'Technical requirements'],
    authorityLevel: 'A3',
    snippet: `Mock excerpt backing the ${name} AI rationale when enableMockApi.scotsman is on.`,
  };
  if (dimension === 'Solution' || dimension === 'Money') {
    return [
      primary,
      {
        id: `chunk:scotsman-mock-${dimension.toLowerCase()}-2`,
        label: 'CRM / qualification notes',
        documentTitle: 'Internal CRM snapshot (mock).md',
        sectionPath: ['Account', 'Qualification'],
        authorityLevel: 'knowledge',
        snippet: 'Second source for multi-citation behaviour in the mock SCOTSMAN draft.',
      },
    ];
  }
  return [primary];
}

function mockAiRationaleWithCitations(dimension: ScotsmanDimension): string {
  const base = MOCK_AI_RATIONALE_BY_DIMENSION[dimension];
  const evidence = mockEvidenceForDimension(dimension);
  if (evidence.length === 0) {
    return base;
  }
  if (evidence.length === 1) {
    return `${base} Corroborated in client materials [1].`;
  }
  return `${base} Corroborated in client materials [1] and internal notes [2].`;
}

const MOCK_AI_SUGGESTED: Partial<Record<ScotsmanDimension, ScotsmanRagStatus>> = {
  Solution: 'Green',
  Competition: 'Amber',
  Originality: 'Amber',
  Timescale: 'Red',
  Size: 'Green',
  Money: 'Green',
  Authority: 'Red',
  Need: 'Green',
};

function ragToNumeric(rag: ScotsmanRagStatus | null | undefined): number | null {
  if (rag === 'Red') return 0;
  if (rag === 'Amber') return 0.5;
  if (rag === 'Green') return 1;
  return null;
}

function computeOverallHumanScore(dimensions: ScotsmanDimensionAssessmentApiResponse[]): number {
  let sum = 0;
  for (const row of dimensions) {
    const n = ragToNumeric(row.humanStatus);
    if (n != null) sum += n * row.weight;
  }
  return Math.min(100, Math.max(0, sum));
}

function computeOverallAiScore(dimensions: ScotsmanDimensionAssessmentApiResponse[]): number {
  let sum = 0;
  for (const row of dimensions) {
    const n = ragToNumeric(row.aiSuggestedStatus);
    if (n != null) sum += n * row.weight;
  }
  return Math.min(100, Math.max(0, sum));
}

function humanCompletenessPercent(dimensions: ScotsmanDimensionAssessmentApiResponse[]): number {
  const assessed = dimensions.filter(
    (d) => d.humanStatus === 'Green' || d.humanStatus === 'Amber' || d.humanStatus === 'Red',
  ).length;
  return (100 * assessed) / SCOTSMAN_DIMENSION_COUNT;
}

function aiCompletenessPercent(dimensions: ScotsmanDimensionAssessmentApiResponse[]): number {
  const assessed = dimensions.filter(
    (d) =>
      d.aiSuggestedStatus === 'Green' ||
      d.aiSuggestedStatus === 'Amber' ||
      d.aiSuggestedStatus === 'Red',
  ).length;
  return (100 * assessed) / SCOTSMAN_DIMENSION_COUNT;
}

const MOCK_DISCOVERY_RUN_ID = 'mock-discovery-run';

function resolveMockAnalyzerProgress(
  assessmentGetCount: number,
  analyzerReadyAfterGetPolls: number,
): { readyAiDimensionCount: number; analyzerStatus: ScotsmanAnalyzerStatus } {
  if (assessmentGetCount < analyzerReadyAfterGetPolls) {
    return { readyAiDimensionCount: 0, analyzerStatus: 'Running' };
  }

  const readyAiDimensionCount = Math.min(
    SCOTSMAN_DIMENSION_COUNT,
    assessmentGetCount - analyzerReadyAfterGetPolls + 1,
  );

  return {
    readyAiDimensionCount,
    analyzerStatus: readyAiDimensionCount >= SCOTSMAN_DIMENSION_COUNT ? 'Completed' : 'Running',
  };
}

function buildDimensionRow(
  dimension: ScotsmanDimension,
  humanStatus: ScotsmanRagStatus | null | undefined,
  humanRationale: string | null | undefined,
  includeAi: boolean,
  lastAnalyzerRunId: string | null = null,
): ScotsmanDimensionAssessmentApiResponse {
  const now = new Date().toISOString();
  const weight = MOCK_DIMENSION_WEIGHTS[dimension];
  const aiSuggestedStatus = includeAi ? (MOCK_AI_SUGGESTED[dimension] ?? null) : null;
  const aiNumeric = ragToNumeric(aiSuggestedStatus);
  const humanNumeric = ragToNumeric(humanStatus);
  const evidence = includeAi ? mockEvidenceForDimension(dimension) : [];
  return {
    dimension,
    weight,
    aiSuggestedStatus,
    humanStatus: humanStatus ?? null,
    aiSuggestedScore: aiNumeric != null ? aiNumeric * weight : null,
    humanScore: humanNumeric != null ? humanNumeric * weight : null,
    aiRationale: includeAi ? mockAiRationaleWithCitations(dimension) : null,
    humanRationale: humanRationale ?? null,
    aiMitigations: [],
    aiConcerns: [],
    confidence: null,
    signalRefs: [],
    findingRefs: [],
    evidenceRefs: evidence.map((item) => item.id),
    evidence,
    clarificationRefs: [],
    suggestedClarificationQuestions: [],
    aiSuggestionChangedSinceHumanReview: false,
    lastAcknowledgedAnalyzerRunId: null,
    lastAnalyzerRunId: includeAi ? lastAnalyzerRunId : null,
    aiGeneratedAtUtc: includeAi ? now : null,
    updatedAtUtc: now,
  };
}

interface ScotsmanOpportunityMockState {
  assessmentGetCount: number;
  humanByDimension: Partial<
    Record<ScotsmanDimension, { humanStatus?: ScotsmanRagStatus; humanRationale?: string }>
  >;
}

function buildAssessment(
  opportunityId: string,
  humanByDimension: Partial<
    Record<ScotsmanDimension, { humanStatus?: ScotsmanRagStatus; humanRationale?: string }>
  >,
  options: { readyAiDimensionCount: number; analyzerStatus: ScotsmanAnalyzerStatus },
): ScotsmanAssessmentApiResponse {
  const readyCount = Math.max(0, Math.min(options.readyAiDimensionCount, SCOTSMAN_DIMENSION_COUNT));
  const dimensions = SCOTSMAN_DIMENSIONS.map((definition, index) =>
    buildDimensionRow(
      definition.dimension,
      humanByDimension[definition.dimension]?.humanStatus,
      humanByDimension[definition.dimension]?.humanRationale,
      index < readyCount,
      MOCK_DISCOVERY_RUN_ID,
    ),
  );
  const now = new Date().toISOString();
  const running = options.analyzerStatus === 'Running';
  return {
    opportunityId,
    overallAiScore: readyCount > 0 ? computeOverallAiScore(dimensions) : 0,
    overallHumanScore: computeOverallHumanScore(dimensions),
    aiCompletenessPercent: readyCount > 0 ? aiCompletenessPercent(dimensions) : 0,
    humanCompletenessPercent: humanCompletenessPercent(dimensions),
    currentVersion: 1,
    analyzerStatus: options.analyzerStatus,
    pendingDiscoveryAssessmentRunId: running ? MOCK_DISCOVERY_RUN_ID : null,
    lastDiscoveryAssessmentRunId:
      readyCount >= SCOTSMAN_DIMENSION_COUNT ? MOCK_DISCOVERY_RUN_ID : null,
    lastAnalyzerRunId: readyCount >= SCOTSMAN_DIMENSION_COUNT ? MOCK_DISCOVERY_RUN_ID : null,
    analyzerStartedAtUtc: running || readyCount > 0 ? now : null,
    analyzerCompletedAtUtc: options.analyzerStatus === 'Completed' ? now : null,
    analyzerError: null,
    dimensions,
  };
}

const stateByOpportunityId = new Map<string, ScotsmanOpportunityMockState>();
const completedAssessmentByOpportunityId = new Map<string, ScotsmanAssessmentApiResponse>();

function getOrCreateOpportunityState(opportunityId: string): ScotsmanOpportunityMockState {
  let state = stateByOpportunityId.get(opportunityId);
  if (!state) {
    state = { assessmentGetCount: 0, humanByDimension: {} };
    stateByOpportunityId.set(opportunityId, state);
  }
  return state;
}

function prepareForAssessmentGet(opportunityId: string): ScotsmanOpportunityMockState {
  const timing = getScotsmanMockTiming();
  const state = getOrCreateOpportunityState(opportunityId);

  if (timing.replayAnalyzerOnEachLoad && completedAssessmentByOpportunityId.has(opportunityId)) {
    const previous = completedAssessmentByOpportunityId.get(opportunityId);
    if (previous) {
      state.humanByDimension = humanStateFromAssessment(previous);
      completedAssessmentByOpportunityId.delete(opportunityId);
      state.assessmentGetCount = 0;
    }
  }

  return state;
}

function buildAssessmentForPoll(
  opportunityId: string,
  state: ScotsmanOpportunityMockState,
): { assessment: ScotsmanAssessmentApiResponse; delayMs: number } {
  const timing = getScotsmanMockTiming();
  state.assessmentGetCount += 1;

  const progress = resolveMockAnalyzerProgress(
    state.assessmentGetCount,
    timing.analyzerReadyAfterGetPolls,
  );
  const assessment = buildAssessment(opportunityId, state.humanByDimension, progress);

  if (progress.analyzerStatus === 'Completed') {
    completedAssessmentByOpportunityId.set(opportunityId, assessment);
  }

  const delayMs =
    state.assessmentGetCount === 1 ? timing.initialLoadDelayMs : timing.pollResponseDelayMs;

  return { assessment, delayMs };
}

function parseDimension(segment: string): ScotsmanDimension | null {
  const normalized = segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase();
  const match = SCOTSMAN_DIMENSIONS.find(
    (d) => d.dimension.toLowerCase() === normalized.toLowerCase(),
  );
  return match?.dimension ?? null;
}

function humanStateFromAssessment(
  assessment: ScotsmanAssessmentApiResponse,
): Partial<
  Record<ScotsmanDimension, { humanStatus?: ScotsmanRagStatus; humanRationale?: string }>
> {
  const out: Partial<
    Record<ScotsmanDimension, { humanStatus?: ScotsmanRagStatus; humanRationale?: string }>
  > = {};
  for (const row of assessment.dimensions) {
    out[row.dimension] = {
      humanStatus: row.humanStatus ?? undefined,
      humanRationale: row.humanRationale ?? undefined,
    };
  }
  return out;
}

/**
 * Simulates initial load + analyzer polling UX.
 * Tune via `environment.mockApiTiming.scotsman` or sessionStorage key
 * `momentum.mock.scotsman.timing` (JSON).
 */
export function mockHandleScotsmanAssessmentGet(canonical: string): ScotsmanMockHandlerResult {
  const match = SCOTSMAN_ASSESSMENT_GET_PATH.exec(canonical);
  const opportunityId = match?.[1] ? decodeURIComponent(match[1]) : null;
  if (!opportunityId) return { status: 400, body: null };

  const timing = getScotsmanMockTiming();
  if (!timing.replayAnalyzerOnEachLoad) {
    const cached = completedAssessmentByOpportunityId.get(opportunityId);
    if (cached) {
      return { status: 200, body: cached, delayMs: timing.pollResponseDelayMs };
    }
  }

  const state = prepareForAssessmentGet(opportunityId);
  const { assessment, delayMs } = buildAssessmentForPoll(opportunityId, state);
  return { status: 200, body: assessment, delayMs };
}

export function mockHandleScotsmanDimensionPatch(
  canonical: string,
  body: unknown,
): ScotsmanMockHandlerResult {
  const match = SCOTSMAN_DIMENSION_PATCH_PATH.exec(canonical);
  const opportunityId = match?.[1] ? decodeURIComponent(match[1]) : null;
  const dimensionSegment = match?.[2] ? decodeURIComponent(match[2]) : null;
  if (!opportunityId || !dimensionSegment) return { status: 400, body: null };

  const dimension = parseDimension(dimensionSegment);
  if (!dimension) return { status: 404, body: null };

  const timing = getScotsmanMockTiming();
  const state = getOrCreateOpportunityState(opportunityId);
  const patch = (body ?? {}) as ScotsmanHumanDimensionPatchRequest;
  const current = state.humanByDimension[dimension] ?? {};
  state.humanByDimension[dimension] = {
    humanStatus: patch.humanStatus !== undefined ? patch.humanStatus : current.humanStatus,
    humanRationale:
      patch.humanRationale !== undefined ? patch.humanRationale : current.humanRationale,
  };

  const progress = resolveMockAnalyzerProgress(
    state.assessmentGetCount,
    timing.analyzerReadyAfterGetPolls,
  );
  const next = buildAssessment(opportunityId, state.humanByDimension, progress);
  if (progress.analyzerStatus === 'Completed') {
    completedAssessmentByOpportunityId.set(opportunityId, next);
  }

  mockQualificationMaterialChangeById(opportunityId, 'scotsman');

  return { status: 200, body: next, delayMs: timing.pollResponseDelayMs };
}

export function mockHandleScotsmanAcknowledgeAiSuggestionChange(
  canonical: string,
): ScotsmanMockHandlerResult {
  const match = SCOTSMAN_ACKNOWLEDGE_AI_CHANGE_PATH.exec(canonical);
  const opportunityId = match?.[1] ? decodeURIComponent(match[1]) : null;
  const dimensionSegment = match?.[2] ? decodeURIComponent(match[2]) : null;
  if (!opportunityId || !dimensionSegment) return { status: 400, body: null };

  const dimension = parseDimension(dimensionSegment);
  if (!dimension) return { status: 404, body: null };

  const timing = getScotsmanMockTiming();
  const state = getOrCreateOpportunityState(opportunityId);
  const progress = resolveMockAnalyzerProgress(
    state.assessmentGetCount,
    timing.analyzerReadyAfterGetPolls,
  );
  const next = buildAssessment(opportunityId, state.humanByDimension, progress);
  const dimensionRow = next.dimensions.find((row) => row.dimension === dimension);
  if (dimensionRow) {
    dimensionRow.aiSuggestionChangedSinceHumanReview = false;
    dimensionRow.lastAcknowledgedAnalyzerRunId = dimensionRow.lastAnalyzerRunId ?? null;
  }
  if (progress.analyzerStatus === 'Completed') {
    completedAssessmentByOpportunityId.set(opportunityId, next);
  }

  return { status: 200, body: next, delayMs: timing.pollResponseDelayMs };
}

export function mockHandleScotsmanAssessmentFinalize(canonical: string): ScotsmanMockHandlerResult {
  const match = SCOTSMAN_ASSESSMENT_FINALIZE_PATH.exec(canonical);
  const opportunityId = match?.[1] ? decodeURIComponent(match[1]) : null;
  if (!opportunityId) return { status: 400, body: null };

  const cached = completedAssessmentByOpportunityId.get(opportunityId);
  const state = getOrCreateOpportunityState(opportunityId);
  const timing = getScotsmanMockTiming();
  const progress = resolveMockAnalyzerProgress(
    state.assessmentGetCount,
    timing.analyzerReadyAfterGetPolls,
  );
  const assessment =
    cached ??
    (progress.analyzerStatus === 'Completed'
      ? buildAssessment(opportunityId, state.humanByDimension, {
          readyAiDimensionCount: SCOTSMAN_DIMENSION_COUNT,
          analyzerStatus: 'Completed',
        })
      : null);

  if (!assessment || assessment.analyzerStatus !== 'Completed') {
    return { status: 404, body: null };
  }

  mockEnqueueValueAndBidRiskAfterScotsmanFinalize(opportunityId);

  return {
    status: 200,
    body: assessment,
  };
}

export function mockEnqueueScotsmanAnalysisAfterDiscoveryFinalize(opportunityId: string): void {
  const state = getOrCreateOpportunityState(opportunityId);
  state.assessmentGetCount = 0;
  completedAssessmentByOpportunityId.delete(opportunityId);

  buildAssessment(opportunityId, state.humanByDimension, {
    readyAiDimensionCount: 0,
    analyzerStatus: 'Running',
  });
}

export function mockEnqueueValueAndBidRiskAfterScotsmanFinalize(opportunityId: string): void {
  mockMarkBidRiskRunning(opportunityId);
  mockResetOpportunityValuePoll(opportunityId);
}

export function mockHandleScotsmanAssessmentAnalyze(
  canonical: string,
  _body: unknown,
): ScotsmanMockHandlerResult {
  const match = SCOTSMAN_ASSESSMENT_ANALYZE_PATH.exec(canonical);
  const opportunityId = match?.[1] ? decodeURIComponent(match[1]) : null;
  if (!opportunityId) return { status: 400, body: null };

  const timing = getScotsmanMockTiming();
  const state = getOrCreateOpportunityState(opportunityId);
  state.assessmentGetCount = 0;
  completedAssessmentByOpportunityId.delete(opportunityId);

  buildAssessment(opportunityId, state.humanByDimension, {
    readyAiDimensionCount: 0,
    analyzerStatus: 'Running',
  });

  return {
    status: 202,
    body: { correlationId: `mock-scotsman-${opportunityId}` },
    delayMs: timing.pollResponseDelayMs,
  };
}
